import {Injectable} from "@angular/core";
import {ITypedJSONSettings, JsonTypes, TypedJSON} from "typedjson";
import {Constructor} from "typedjson/js/typedjson/types";
import {DxLogger} from "./dx-logger.service";
import {DxConfigService} from "./dx-config.service";
import {PitContractStage} from "../../pit-contracts/domain/pit-contract-stage";


/**
 * Cервис предоставляет функционал:
 * 1. Серилизации  TS-классов в JSON-объекты
 * 2. Десерилизации JSON-объектов (в том числе коллекций) в TS-классы.
 *
 * "Зачем?" - спросите вы. Основная боль любого вэб-приложения - прямая завязка на поля из JSON-ответа бэкэнда.
 * Таким образом, поменяв имя переменной в DTO на бэке, мы рискуем уложить какой-либо код на фронте.
 * Библиотека TypedJSON - позволяет гибко развязать этот узел, хоть и накладывает дополнительные накладные расходы.
 *
 * Нестатические методы определены для возможности выполнения конвертации с возможностью логгирования (возможно еще что-то добавится).
 *
 * Статические версии методов определены для возможности сериализации \ десериализации
 * в статичных методах-фабриках. По-факту это необходимо для работы с ин-мемори бэкэндом.
 *
 * @publicApi
 */
@Injectable()
export class DxObjectMapper {
  /**
   * @constructor
   * @param {DxConfigService} _configService - сервис, предоставляющий доступ к конфигурации ядра.
   * @param {DxLogger} _logger - логгер.
   */
  constructor(private _configService: DxConfigService,
              private _logger: DxLogger) {
    TypedJSON.setGlobalConfig({preserveNull: true});
  }

  /**
   *  Нестатические методы определены для возможности выполнения конвертации с возможностью
   *  логгирования (возможно еще что-то добавится)
   **/

  /**
   * Сериализует типизированный `ts`-объект в нетипизированный `JSON`-объект на основе аннотаций.
   * @param {T} obj - типизированный `ts`-объект в нетипизированный.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {any} - нетипизированный `JSON`-объект
   * @public
   */
  public serialize<T>(obj: T, constructor: Constructor<T>, settings?: ITypedJSONSettings): any {
    const result = TypedJSON.stringify<T>(obj, constructor, settings);
    if(this._configService.logObjectMapping) this._logConversion('serialize', obj, result);
    return result;
  }

  public serializeASArray<T>(obj: T[], constructor: Constructor<T>, dimensions?: 1, settings?: ITypedJSONSettings): any {
    const result = TypedJSON.stringifyAsArray<T>(obj, constructor, dimensions, settings);
    return result;
  }

  /**
   * Десериализует нетипизированный `JSON`-объект в типизированный `ts`-объект на основе аннотаций.
   * @param {any} json - нетипизированный `JSON`-объект.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {T} - типизированный `ts`-объект
   * @public
   */
  public deserialize<T>(json: any, constructor: Constructor<T>) : T {
    const result = TypedJSON.parse<T>(json, constructor);
    if(this._configService.logObjectMapping) this._logConversion('deserialize', json, result);
    return result;
  }

  /**
   * Сериализует типизированный `ts`-объект в `JsonTypes`-объект на основе аннотаций.
   * @param {T} obj - типизированный `ts`-объект в нетипизированный.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {JsonTypes} - `JsonTypes`-объект
   * @public
   */
  public toPlainJson<T>(obj: T, constructor: Constructor<T>): JsonTypes {
    const result = TypedJSON.toPlainJson<T>(obj, constructor);
    if(this._configService.logObjectMapping) this._logConversion('toPlainJson', obj, result);
    return result;
  }

  /**
   * Сериализует массив типизированных `ts`-объектов в массив `JSON`-объектов на основе аннотаций.
   * @param {T[]} obj - массив типизированных `ts`-объектов.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @param {ITypedJSONSettings} settings - настроки десериализации.
   * @param {number} dimensions - размерность массива.
   * @return {Object[]} - массив `JSON`-объектов
   * @public
   */
  public toPlainArray<T>(obj: T[], constructor: Constructor<T>, dimensions?: 1, settings?: ITypedJSONSettings): Object[] {
    const result = TypedJSON.toPlainArray<T>(obj, constructor, dimensions, settings);
    if(this._configService.logObjectMapping) this._logConversion('toPlainArray', obj, result);
    return result;
  }

  /**
   * Десериализует нетипизированный `JSON`-объект в массив типизированных `ts`-объектов на основе аннотаций.
   * @param {any} json - нетипизированный `JSON`-объект.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @param {ITypedJSONSettings} settings - настроки десериализации.
   * @param {number} dimensions - размерность массива.
   * @return {T} - типизированный `ts`-объект
   * @public
   */
  public deserializeASArray<T>(json: any, constructor: Constructor<T>, settings?: ITypedJSONSettings, dimensions?: 1) : T[] {
    const result = TypedJSON.parseAsArray<T>(json, constructor);
    if(this._configService.logObjectMapping) this._logConversion('deserializeASArray', json, result);
    return result;
  }

  /**
   * Десериализует нетипизированный `JSON`-объект в массив типизированных `ts`-объектов на основе аннотаций.
   * @param {any} json - нетипизированный `JSON`-объект.
   * @param {Constructor<K>} keyType - конструктор `ts`-объекта ключа мапы.
   * @param {Constructor<V>} valueType - конструктор `ts`-объекта value мапы.
   * @param {ITypedJSONSettings} settings - настроки десериализации.
   * @return {Map<K, V>} - мапа `ts`-объектов
   * @public
   */
  public deserializeASMap<K, V>(json: any, keyType: Constructor<K>, valueType: Constructor<V>, settings?: ITypedJSONSettings) : Map<K, V> {
    const result = TypedJSON.parseAsMap<K, V>(json, keyType, valueType, settings);
    if(this._configService.logObjectMapping) this._logConversion('deserializeASMap', json, result);
    return result;
  }

  /**
   * @remark Статическая версия метода.
   * Сериализует типизированный `ts`-объект в нетипизированный `JSON`-объект на основе аннотаций.
   * @param {T} obj - типизированный `ts`-объект в нетипизированный.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {any} - нетипизированный `JSON`-объект
   * @public
   */
  public static serialize<T>(obj: T, constructor: Constructor<T>) : any {
    return TypedJSON.stringify<T>(obj, constructor);
  }

  /**
   * @remark Статическая версия метода.
   * Десериализует нетипизированный `JSON`-объект в типизированный `ts`-объект на основе аннотаций.
   * @param {any} json - нетипизированный `JSON`-объект.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {T} - типизированный `ts`-объект
   * @public
   */
  public static deserialize<T>(json: any, constructor: Constructor<T>) : T {
    return TypedJSON.parse<T>(json, constructor);
  }

  /**
   * Десериализует нетипизированный `JSON`-объект в массив типизированных `ts`-объектов на основе аннотаций.
   * @param {any} json - нетипизированный `JSON`-объект.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @param {ITypedJSONSettings} settings - настроки десериализации.
   * @param {number} dimensions - размерность массива.
   * @return {T} - типизированный `ts`-объект
   * @public
   */
  public static deserializeASArray<T>(json: any, constructor: Constructor<T>, settings?: ITypedJSONSettings, dimensions?: 1) : T[] {
    const result = TypedJSON.parseAsArray<T>(json, constructor);
    return result;
  }

  /**
   * Сериализует типизированный `ts`-объект в нетипизированный `JSON`-объект на основе аннотаций.
   * @param {T} obj - типизированный `ts`-объект в нетипизированный.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @return {any} - нетипизированный `JSON`-объект
   * @public
   */
  public static serializeASArray<T>(obj: T[], constructor: Constructor<T>, dimensions?: 1, settings?: ITypedJSONSettings) : any {
    const result = TypedJSON.stringifyAsArray<T>(obj, constructor, dimensions, settings);
    return result;
  }
  /**
   * it realizes deep cloning of only json fields
   */
  public static clone<T>(typedObject: T, constructor: Constructor<T>): T {
    return typedObject ? DxObjectMapper.deserialize(DxObjectMapper.serialize(typedObject, constructor), constructor) : typedObject;
  }

  /**
   * @remark Статическая версия метода.
   * Сериализует типизированный `ts`-объект в `JsonTypes`-объект на основе аннотаций.
   * @param {T} obj - типизированный `ts`-объект в нетипизированный.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @param {ITypedJSONSettings} settings - настроки сериализации.
   * @return {JsonTypes} - `JsonTypes`-объект
   * @public
   */
  public static toPlainJson<T>(obj: T, constructor: Constructor<T>, settings?: ITypedJSONSettings): JsonTypes {
    return TypedJSON.toPlainJson<T>(obj, constructor, settings);
  }

  /**
   * Сериализует массив типизированных `ts`-объектов в массив `JSON`-объектов на основе аннотаций.
   * @param {T[]} obj - массив типизированных `ts`-объектов.
   * @param {Constructor<T>} constructor - конструктор `ts`-объекта.
   * @param {ITypedJSONSettings} settings - настроки десериализации.
   * @param {number} dimensions - размерность массива.
   * @return {Object[]} - массив `JSON`-объектов
   * @public
   */
  public static toPlainArray<T>(obj: T[], constructor: Constructor<T>, dimensions?: 1, settings?: ITypedJSONSettings): Object[] {
    return TypedJSON.toPlainArray<T>(obj, constructor, dimensions, settings);
  }

  /**
   * Логгирует операцию конвертации.
   * @param {string} method - имя вызываемого метода.
   * @param {any} input - входной объект.
   * @param {any} output - результат конвертации.
   */
  private _logConversion(method: string, input: any, output: any): void {
    this._logger.debug('Call: ' + method + '(...)');
    this._logger.debug('Input: ');
    this._logger.debug(input);
    this._logger.debug('Output: ');
    this._logger.debug(output);
  }
}
